import { collection, deleteDoc, doc, getDocs, setDoc, updateDoc, db } from "../../../services/firestoreApi";
import { useContext, useEffect, useRef, useState } from "react";
import { Dropdown, Table } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { v4 as uuidv4 } from "uuid";
import CustomSpinner from "../../general/CustomSpinner";
import UserContext from "../../user/UserContext";
import { getCoordinates } from "../../../services/beneficiosService";
import { canManageAffiliates } from "./affiliatePermissions";
import AdminPagination, { ITEMS_PER_PAGE } from "./AdminPagination";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const STATUS = { precargado: "pre-cargado", cargado: "cargado" }
const FIELDS = [
    ["name", "Nombre"], ["lastname_1", "Primer apellido"], ["lastname_2", "Segundo apellido"],
    ["colonia", "Colonia"], ["calle", "Calle"], ["domicilio_numero", "Número"],
    ["cp", "Código postal"], ["curp", "CURP"], ["elector_key", "Clave de elector"],
    ["seccion", "Sección"], ["phone", "Teléfono"], ["fechaRegistro", "Fecha de registro"]
]

const emptyAfiliado = {
    name: "", lastname_1: "", lastname_2: "", colonia: "", calle: "", domicilio_numero: "",
    cp: "", curp: "", elector_key: "", seccion: "", phone: "", fechaRegistro: "", latitud: null, longitud: null,
    Status: STATUS.cargado, folio: "", registradoPorNombre: ""
}

const formValue = (field, value) => field === "fechaRegistro" && value?.toDate
    ? value.toDate().toISOString().slice(0, 10)
    : value || ""

const buildAddress = afiliado => [
    afiliado.calle, afiliado.domicilio_numero, afiliado.colonia,
    afiliado.cp, afiliado.municipio, afiliado.estado, "México"
].filter(Boolean).join(", ")

const MapFilterDropdown = ({ label, options, selected, onChange }) => (
    <Dropdown autoClose="outside">
        <Dropdown.Toggle variant="light" className="admin-map-filter-toggle w-100 text-start">
            {label}{selected.length ? ` (${selected.length})` : ""}
        </Dropdown.Toggle>
        <Dropdown.Menu className="admin-map-filter-menu w-100">
            {options.map(option => <label key={option} className="dropdown-item">
                <input className="form-check-input me-2" type="checkbox"
                    checked={selected.includes(option)} onChange={() => onChange(option)} />
                {option}
            </label>)}
        </Dropdown.Menu>
    </Dropdown>
)

const AfiliadoForm = ({ value, onChange, isNew }) => FIELDS
    .filter(([field]) => !isNew || field !== "fechaRegistro")
    .map(([field, label]) => (
    <span key={field}>
        <label className="form-label" >
            {label}

        </label>
        <input className="form-control mb-3" name={field} type={field === "fechaRegistro" ? "date" : "text"}
            value={formValue(field, value[field])} onChange={onChange} />
    </span>
    ))

const AfiliadosCRUD = () => {
    const [data, setData] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [modal, setModal] = useState("")
    const [selected, setSelected] = useState(emptyAfiliado)
    const [selectedId, setSelectedId] = useState("")
    const [modalMapa, setModalMapa] = useState(false)
    const [promotoresMapa, setPromotoresMapa] = useState([])
    const [seccionesMapa, setSeccionesMapa] = useState([])
    const mapaRef = useRef(null)
    const { user, loading, setLoading } = useContext(UserContext)
    const hasAffiliateAccess = canManageAffiliates(user)

    useEffect(() => {
        if (!modalMapa || !mapaRef.current || !hasAffiliateAccess) return

        const ubicaciones = data.map(item => ({ ...item.data(), id: item.id }))
            .filter(item => Number.isFinite(Number(item.latitud)) && Number.isFinite(Number(item.longitud)))
            .filter(item => !promotoresMapa.length || promotoresMapa.includes(item.registradoPorNombre || "Sin registro"))
            .filter(item => !seccionesMapa.length || seccionesMapa.includes(String(item.seccion || "Sin sección")))
        const mapa = L.map(mapaRef.current).setView([19.4194, -102.0637], 12)
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "&copy; OpenStreetMap contributors" }).addTo(mapa)
        const puntos = ubicaciones.map(item => {
            const punto = [Number(item.latitud), Number(item.longitud)]
            L.marker(punto).addTo(mapa).bindPopup(`<strong>${item.folio || "Sin folio"}</strong><br>${item.name || "Afiliado"}`)
            return punto
        })

        if (puntos.length > 1) mapa.fitBounds(puntos, { padding: [30, 30] })
        setTimeout(() => mapa.invalidateSize(), 0)
        return () => mapa.remove()
    }, [modalMapa, data, hasAffiliateAccess, promotoresMapa, seccionesMapa])

    const getItems = async () => {
        setLoading(true)
        const snapshot = await getDocs(collection(db, "afiliados"))
        setData(snapshot.docs)
        setCurrentPage(1)
        setLoading(false)
    }

    const handleChange = ({ target }) => setSelected(previous => ({ ...previous, [target.name]: target.value }))

    const save = async event => {
        event.preventDefault()
        setLoading(true)
        const payload = {
            ...selected,
            name: selected.name.trim(),
            curp: selected.curp.trim().toUpperCase(),
            elector_key: selected.elector_key.trim().toUpperCase(),
            fechaRegistro: selected.fechaRegistro || new Date().toISOString().slice(0, 10),
            Status: selected.Status || STATUS.cargado,
            registradoPorNombre: selectedId ? selected.registradoPorNombre : user?.data?.name || ""
        }
        const coordinates = await getCoordinates(buildAddress(payload))
        const id = selectedId || uuidv4()
        await (selectedId ?
            updateDoc(doc(db, "afiliados", id), { ...payload, ...coordinates })
            :
            setDoc(doc(db, "afiliados", id), { ...payload, ...coordinates }))
        await getItems()
        setModal("")
        setLoading(false)
    }

    const openForm = item => {
        setSelectedId(item?.id || "")
        setSelected(item
            ? { ...emptyAfiliado, ...item.data() }
            : { ...emptyAfiliado, fechaRegistro: new Date().toISOString().slice(0, 10), Status: STATUS.cargado })
        setModal("form")
    }

    const remove = async () => {
        setLoading(true)
        await deleteDoc(doc(db, "afiliados", selectedId))
        await getItems()
        setModal("")
        setLoading(false)
    }

    const exportCsv = async () => {
        const snapshot = await getDocs(collection(db, "afiliados"))
        const fields = [...FIELDS.map(([field]) => field), "registradoPorNombre", "latitud", "longitud", "Status", "folio"]
        const escape = value => `"${String(value ?? "").replace(/"/g, '""')}"`
        const rows = snapshot.docs.map(item => fields.map(field => escape(field === "latitud"
            ? item.data().latitud : field === "longitud" ? item.data().longitud : item.data()[field])).join(","))
        const link = document.createElement("a")
        link.href = URL.createObjectURL(new Blob(["\ufeff" + [fields.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8" }))
        link.download = "afiliados.csv"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
    }
    

    const visibleData = data.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
    const mapPromotorOptions = [...new Set(data.map(item => item.data().registradoPorNombre || "Sin registro"))].sort()
    const mapSectionOptions = [...new Set(data.map(item => String(item.data().seccion || "Sin sección")))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))

    const toggleMapFilter = (setter, value) => {
        setter(previous => previous.includes(value)
            ? previous.filter(item => item !== value)
            : [...previous, value])
    }

    return <>
        {loading && <CustomSpinner />}
        <div className="container text-center my-5 admin-module admin-module-affiliates">
            <h1>Afiliados <button className="btn text-white bg-indigo m-5" onClick={() => openForm()}>Nuevo afiliado</button></h1>
            <input type="button" className="btn btn-primary mb-5 me-2" value="Ver afiliados" onClick={getItems} />
            {hasAffiliateAccess && <input type="button" className="btn btn-success mb-5" value="Descargar CSV" onClick={exportCsv} />}
            {hasAffiliateAccess && <input type="button" className="btn btn-secondary mb-5 ms-2" value="Ver en el mapa" onClick={() => setModalMapa(true)} disabled={!data.length} />}
            {data.length > 0 && <>
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Folio</th>
                            <th>Nombre</th>
                            <th>Registrado por</th>
                            <th>WhatsApp</th>
                            <th>CURP</th>
                            <th>Colonia</th>
                            <th>Estado</th>
                            <th>Coordenadas</th>
                            <th>Opciones</th>
                        </tr>
                    </thead>
                    <tbody>{visibleData.map(item => {
                        const afiliado = item.data()
                        const precargado = afiliado.Status === STATUS.precargado
                        return <tr key={item.id} className={precargado ? "afiliado-pre-cargado" : "afiliado-cargado"}>
                            <td>{afiliado.folio || "Sin folio"}</td>
                            <td>{[afiliado.name, afiliado.lastname_1, afiliado.lastname_2].filter(Boolean).join(" ")}</td>
                            <td>{afiliado.registradoPorNombre || "Sin registro"}</td>
                            <td>{afiliado.phone}</td><td>{afiliado.curp || "Pendiente"}</td>
                            <td>{afiliado.colonia}</td>
                            <td><span className={`afiliado-status ${precargado ? "is-pre-cargado" : "is-cargado"}`}>{afiliado.Status || STATUS.cargado}</span></td>
                            <td>{afiliado.latitud ? `${afiliado.latitud}, ${afiliado.longitud}` : "Sin ubicación"}</td>
                            <td><button className="btn btn-primary m-1" onClick={() => openForm(item)}>Editar</button><button className="btn btn-danger" onClick={() => { setSelectedId(item.id); setModal("delete") }}>Eliminar</button></td>
                        </tr>
                    })}</tbody>
                </Table>
                <AdminPagination totalItems={data.length} currentPage={currentPage} onPageChange={setCurrentPage} />
            </>}
        </div>

        <Modal show={modal === "form"} onHide={() => setModal("")}>
            <Modal.Header closeButton><Modal.Title>{selectedId ? "Editar afiliado" : "Nuevo afiliado"}</Modal.Title></Modal.Header>
            <form onSubmit={save}><Modal.Body>
                <AfiliadoForm value={selected} onChange={handleChange} isNew={!selectedId} />
                {selectedId && <label className="form-label">Estado<select className="form-select" name="Status" value={selected.Status} onChange={handleChange}><option value={STATUS.precargado}>{STATUS.precargado}</option><option value={STATUS.cargado}>{STATUS.cargado}</option></select></label>}
            </Modal.Body><Modal.Footer><button className="btn btn-primary" type="submit">Guardar</button><button className="btn btn-danger" type="button" onClick={() => setModal("")}>Cancelar</button></Modal.Footer></form>
        </Modal>
        <Modal show={modal === "delete"} onHide={() => setModal("")}><Modal.Header closeButton>Eliminar afiliado</Modal.Header><Modal.Body>¿Estás seguro de eliminar este afiliado?</Modal.Body><Modal.Footer><button className="btn btn-danger" onClick={remove}>Sí</button><button className="btn btn-primary" onClick={() => setModal("")}>No</button></Modal.Footer></Modal>
        <Modal show={modalMapa} onHide={() => setModalMapa(false)} fullscreen>
            <Modal.Header closeButton><Modal.Title>Ubicación de los afiliados</Modal.Title></Modal.Header>
            <Modal.Body className="admin-benefits-map-body">
                <div className="row g-3 p-3">
                    <div className="col-md-6">
                        <MapFilterDropdown label="Promotores" options={mapPromotorOptions}
                            selected={promotoresMapa} onChange={value => toggleMapFilter(setPromotoresMapa, value)} />
                    </div>
                    <div className="col-md-6">
                        <MapFilterDropdown label="Secciones" options={mapSectionOptions}
                            selected={seccionesMapa} onChange={value => toggleMapFilter(setSeccionesMapa, value)} />
                    </div>
                    <button type="button" className="btn btn-outline-secondary"
                        onClick={() => { setPromotoresMapa([]); setSeccionesMapa([]) }}>
                    Limpiar filtros
                    </button>
                </div>
                <div ref={mapaRef} className="admin-benefits-map" />
            </Modal.Body>
        </Modal>
    </>
}

export default AfiliadosCRUD
